import React, { useState, useMemo } from 'react';
import {
  Search, BookOpen, Shield, Zap, FileText, ChevronRight,
  ChevronDown, AlertTriangle, Clock, Tag, ArrowLeft,
  CheckCircle, Terminal, Globe, Lock, Activity, Users,
  Database, Eye, BarChart2, AlertCircle, Info, Copy, Check,
} from 'lucide-react';
import './knowledge.scss';

// ─── Data ─────────────────────────────────────────────────────────────────────

const ARTICLES = [
  // ══ INCIDENT RESPONSE SOPs ══
  {
    id: 'sop-ir-001',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-001: Incident Triage & Initial Response',
    severity: 'critical',
    readTime: '5 min',
    tags: ['triage', 'p1', 'all-incidents'],
    summary: 'Standard procedure for receiving, validating and prioritising any security alert before full investigation begins.',
    sections: [
      {
        title: 'Objective',
        content: 'Ensure every security alert is validated, correctly classified, assigned and actioned within defined SLA windows, preventing alert fatigue while guaranteeing no true positive is missed.',
      },
      {
        title: 'Severity & SLA Matrix',
        type: 'table',
        headers: ['Severity', 'Description', 'Initial Response SLA', 'Escalation SLA'],
        rows: [
          ['Critical (P1)', 'Active breach, ransomware, data exfiltration in progress', '15 minutes', '30 minutes'],
          ['High (P2)', 'Compromised account, lateral movement, C2 detected', '1 hour', '4 hours'],
          ['Medium (P3)', 'Suspicious behaviour, policy violation, recon activity', '4 hours', '24 hours'],
          ['Low (P4)', 'Informational, single failed login, port scan', '24 hours', '72 hours'],
        ],
      },
      {
        title: 'Step-by-Step Procedure',
        type: 'steps',
        steps: [
          { label: 'Receive & Acknowledge', detail: 'Acknowledge the alert in VorXOC within SLA. Do not close without investigation. Record receipt timestamp.' },
          { label: 'Initial Validation', detail: 'Check if the alert is a known false positive. Cross-reference with recent changes, maintenance windows, and known-good baselines. If false positive → document reason and close with FP tag.' },
          { label: 'Assign Severity', detail: 'Use the Severity Matrix above. Err on the side of higher severity if unsure — you can downgrade after investigation.' },
          { label: 'Create Incident Ticket', detail: 'Open a formal incident in VorXOC. Populate: title, severity, affected assets, initial description, time of first occurrence.' },
          { label: 'Assign Analyst', detail: 'Assign the incident to the appropriate tier analyst. P1/P2 → Tier 2 or 3. P3/P4 → Tier 1 with escalation path defined.' },
          { label: 'Notify Stakeholders', detail: 'P1: Notify SOC Lead + Customer immediately by phone + ticket. P2: Notify via ticket + email within 30 min. P3/P4: Ticket notification only.' },
          { label: 'Initial Containment Check', detail: 'Before deep investigation: Can the threat spread? If yes, initiate immediate containment (see SOP-IR-002 through SOP-IR-007). Do not investigate an active threat without containment.' },
          { label: 'Document Timeline', detail: 'Record all actions taken with timestamps in the incident ticket. This is required for post-incident review and potential legal proceedings.' },
        ],
      },
      {
        title: 'Escalation Contacts',
        type: 'info',
        content: 'P1 Escalation: SOC Manager → CISO → Customer CTO/CISO\nP2 Escalation: SOC Lead → SOC Manager\nOut-of-hours: Use on-call rotation. Never leave a P1 unattended.',
      },
    ],
  },
  {
    id: 'sop-ir-002',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-002: Malware Detection & Response',
    severity: 'high',
    readTime: '8 min',
    tags: ['malware', 'endpoint', 'containment', 'forensics'],
    summary: 'Step-by-step response to confirmed malware detection on endpoints, including isolation, evidence collection, eradication and recovery.',
    sections: [
      {
        title: 'Objective',
        content: 'Contain and eradicate malware from affected systems while preserving forensic evidence and minimising business disruption.',
      },
      {
        title: 'Immediate Actions (First 30 Minutes)',
        type: 'steps',
        steps: [
          { label: 'Isolate the Host', detail: 'Via EDR console (CrowdStrike/Defender), network isolation or physical disconnection. Document isolation time. Do NOT power off — volatile memory data will be lost.' },
          { label: 'Capture Volatile Data', detail: 'Running processes (tasklist /v), network connections (netstat -ano), logged-on users (query user), loaded modules. Use EDR live response if available.' },
          { label: 'Preserve Memory', detail: 'If possible and time permits, take a memory dump before any remediation. Tools: WinPmem, Magnet RAM Capture. Store on isolated forensic share.' },
          { label: 'Identify Malware Family', detail: 'Check EDR detection name. Upload sample hash to VirusTotal. Check Sentinel alerts for MITRE technique mapping. Classify: RAT / Ransomware / Stealer / Rootkit / Worm.' },
          { label: 'Determine Blast Radius', detail: 'Query Sentinel: same malware hash across other hosts, same C2 IP, same user account, lateral movement (Event ID 4624 type 3). Check for persistence mechanisms.' },
        ],
      },
      {
        title: 'Eradication & Recovery',
        type: 'steps',
        steps: [
          { label: 'Collect Forensic Artifacts', detail: 'Prefetch files, event logs (Security, System, Application), browser history, scheduled tasks, registry run keys, recently modified files. Use forensic imaging if required.' },
          { label: 'Remove Malware', detail: 'EDR-initiated remediation is preferred. Manual: delete malware files, remove persistence (scheduled tasks, registry keys, services). Verify with hash comparison.' },
          { label: 'Reset Credentials', detail: 'Reset all accounts that authenticated from the compromised host. Revoke active sessions. Check for credential dumping (Event ID 4104, LSASS access).' },
          { label: 'Rebuild or Restore', detail: 'For severe infections: full OS rebuild from gold image. For minor infections: verified clean-up + monitoring. Restore data from last known-good backup.' },
          { label: 'Re-integrate Host', detail: 'Remove network isolation. Monitor closely for 48 hours with enhanced logging. Verify EDR agent is reporting clean.' },
          { label: 'Post-Incident Review', detail: 'Document full timeline, IOCs identified, root cause, containment actions, gaps found. Share IOCs with threat intel platform.' },
        ],
      },
      {
        title: 'Key Sentinel KQL Queries',
        type: 'code',
        lang: 'kql',
        snippets: [
          { label: 'Find malware hash across fleet', code: 'DeviceFileEvents\n| where SHA256 == "<hash>"\n| distinct DeviceName, FolderPath, InitiatingProcessFileName\n| sort by DeviceName' },
          { label: 'Detect LSASS credential dumping', code: 'DeviceProcessEvents\n| where FileName == "lsass.exe"\n| where InitiatingProcessFileName != "wininit.exe"\n| project Timestamp, DeviceName, InitiatingProcessFileName, InitiatingProcessCommandLine' },
          { label: 'C2 beaconing pattern', code: 'DeviceNetworkEvents\n| where TimeGenerated > ago(24h)\n| summarize Count=count(), LastSeen=max(TimeGenerated) by DeviceName, RemoteIP, RemotePort\n| where Count > 50\n| sort by Count desc' },
        ],
      },
    ],
  },
  {
    id: 'sop-ir-003',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-003: Phishing & Business Email Compromise',
    severity: 'high',
    readTime: '7 min',
    tags: ['phishing', 'email', 'bec', 'credentials'],
    summary: 'Response procedure for confirmed phishing attacks, credential harvesting, and Business Email Compromise (BEC) scenarios.',
    sections: [
      {
        title: 'Objective',
        content: 'Neutralise the phishing campaign, protect compromised accounts, prevent financial/data loss, and identify all affected users.',
      },
      {
        title: 'Immediate Response Steps',
        type: 'steps',
        steps: [
          { label: 'Quarantine the Email', detail: 'Remove from all mailboxes using Exchange/M365 purge or equivalent. Record: sender, subject, sent time, all recipients, all links and attachments.' },
          { label: 'Analyse the Email', detail: 'Check headers for true originating IP (Received: from headers). Analyse URLs — use sandbox (Any.run, Hybrid Analysis). Extract and hash any attachments without opening. Check DKIM/DMARC/SPF results.' },
          { label: 'Identify All Recipients', detail: 'Query email gateway logs. Check if any users clicked links (Defender ATP Safe Links, proxy logs). Check if any attachments were opened (EDR file execution logs).' },
          { label: 'Check for Credential Compromise', detail: 'For users who clicked: check Entra ID / Okta sign-in logs for anomalous logins (new IP, new country, impossible travel). Check for MFA bypass or token theft.' },
          { label: 'Reset Compromised Accounts', detail: 'Force password reset. Revoke all active sessions and refresh tokens. Revoke app consent grants. Re-enroll MFA if suspected TOTP seed compromise.' },
        ],
      },
      {
        title: 'BEC-Specific Actions',
        type: 'steps',
        steps: [
          { label: 'Check Email Forwarding Rules', detail: 'Review all inbox rules for: forward to external, delete matching rules, move to folder. Remove any unauthorised rules immediately.' },
          { label: 'Review OAuth App Permissions', detail: 'In Entra ID → Enterprise Apps: look for newly consented apps with Mail.Read, Mail.Send, Contacts.Read permissions. Revoke unauthorised consents.' },
          { label: 'Check for Financial Fraud', detail: 'If BEC involved finance: immediately contact finance team. Recall or reverse any transfers initiated within the compromise window. Contact bank if wire transfer occurred.' },
          { label: 'Notify Affected Users', detail: 'Send clear communication: what happened, what they should do (change passwords, watch for follow-up), what IT is doing.' },
          { label: 'Block IOCs', detail: 'Block sending domain/IP in email gateway. Add URL to web filter block list. Add attachment hashes to EDR block list.' },
        ],
      },
      {
        title: 'Key Sentinel KQL Queries',
        type: 'code',
        lang: 'kql',
        snippets: [
          { label: 'Detect impossible travel', code: 'SigninLogs\n| where ResultType == 0\n| extend City = tostring(LocationDetails.city), Country = tostring(LocationDetails.countryOrRegion)\n| summarize Countries=make_set(Country), Cities=make_set(City) by UserPrincipalName, bin(TimeGenerated, 2h)\n| where array_length(Countries) > 1' },
          { label: 'Find suspicious inbox rules', code: 'OfficeActivity\n| where Operation == "Set-Mailbox" or Operation == "New-InboxRule"\n| where Parameters contains "ForwardTo" or Parameters contains "DeleteMessage"\n| project TimeGenerated, UserId, ClientIP, Parameters' },
          { label: 'OAuth app consent events', code: 'AuditLogs\n| where OperationName == "Consent to application"\n| extend AppName = tostring(TargetResources[0].displayName)\n| project TimeGenerated, InitiatedBy, AppName, ResultDescription' },
        ],
      },
    ],
  },
  {
    id: 'sop-ir-004',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-004: Ransomware Response',
    severity: 'critical',
    readTime: '10 min',
    tags: ['ransomware', 'p1', 'containment', 'recovery'],
    summary: 'Emergency response procedure for confirmed ransomware infection. Prioritises containment to prevent spread and preserve recovery options.',
    sections: [
      {
        title: '⚠️ Critical First Rule',
        type: 'warning',
        content: 'DO NOT PAY THE RANSOM without legal/executive authorisation. Payment does not guarantee decryption and may violate sanctions law. Immediately escalate to SOC Manager and customer executive team.',
      },
      {
        title: 'Immediate Containment (First 15 Minutes)',
        type: 'steps',
        steps: [
          { label: 'STOP — Assess Before Acting', detail: 'Confirm ransomware is active (encryption in progress) vs. already completed. If active: immediate network isolation is mandatory.' },
          { label: 'Network Isolation', detail: 'Isolate ALL affected segments. Use firewall rules, VLAN segmentation, or physical disconnection. Block SMB (445), RDP (3389), and any identified C2 IPs at perimeter immediately.' },
          { label: 'DO NOT Power Off', detail: 'Keep systems running — memory contains encryption keys, process info, and C2 communication that is vital for forensics and possible decryption.' },
          { label: 'Identify Patient Zero', detail: 'Review Sentinel: first system with ransomware IOC, earliest encryption event, authentication logs for lateral movement path.' },
          { label: 'Identify Ransomware Variant', detail: 'Check ransom note filename and contents. Use ID Ransomware (nomoreransom.org) with ransom note or encrypted file extension. Identify known decryptors before paying anything.' },
          { label: 'Determine Scope', detail: 'How many hosts affected? Are backups encrypted? Are domain controllers affected? Is Active Directory compromised? This determines recovery path.' },
        ],
      },
      {
        title: 'Escalation & Communication',
        type: 'steps',
        steps: [
          { label: 'Escalate to SOC Manager', detail: 'Immediate phone call. Provide: number of affected hosts, whether backups are intact, ransomware variant if identified, business systems affected.' },
          { label: 'Notify Customer Executive', detail: 'Customer CTO/CISO notification within 15 minutes. Provide situation report and initial containment actions taken.' },
          { label: 'Legal / Cyber Insurance', detail: 'Notify legal counsel. Contact cyber insurance carrier — many require notification within hours. Do not communicate publicly or to attackers without legal guidance.' },
          { label: 'Regulatory Notification', detail: 'Assess GDPR/HIPAA/PCI obligations. If personal data is involved and breach threshold met, 72-hour GDPR notification clock starts now.' },
        ],
      },
      {
        title: 'Recovery Path',
        type: 'steps',
        steps: [
          { label: 'Assess Backup Integrity', detail: 'Are offline/immutable backups available and uncompromised? Verify backup integrity before restoration. Check backup retention dates vs. first encryption event.' },
          { label: 'Domain Controller Recovery', detail: 'If DCs are compromised: follow Microsoft DART guidance for forest recovery. Assume krbtgt compromise — double reset krbtgt password with 10-hour wait between resets.' },
          { label: 'Staged Recovery', detail: 'Recover in priority order: (1) Critical business systems (2) Authentication infrastructure (3) Secondary systems (4) Endpoints. Isolate recovered systems until full clean confirmed.' },
          { label: 'Root Cause Analysis', detail: 'Identify initial access vector (phishing, RDP brute force, VPN exploit, supply chain). Remediate before bringing systems back online.' },
          { label: 'Post-Incident Hardening', detail: 'Implement: MFA on all accounts, privileged access workstations, offline backup verification, EDR on all endpoints, network segmentation review.' },
        ],
      },
    ],
  },
  {
    id: 'sop-ir-005',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-005: Data Exfiltration Response',
    severity: 'critical',
    readTime: '8 min',
    tags: ['data-breach', 'exfiltration', 'dlp', 'gdpr'],
    summary: 'Response to confirmed or suspected data exfiltration. Covers evidence preservation, regulatory obligations, and remediation.',
    sections: [
      {
        title: 'Objective',
        content: 'Stop ongoing exfiltration, determine what data was taken, meet regulatory notification obligations, and prevent recurrence.',
      },
      {
        title: 'Immediate Containment',
        type: 'steps',
        steps: [
          { label: 'Block Exfiltration Channel', detail: 'Identify exfiltration method: web upload, email, FTP, DNS tunneling, cloud sync. Block at proxy/firewall. Do NOT remove evidence before blocking.' },
          { label: 'Preserve Evidence', detail: 'Capture network logs showing data transfer (size, destination, protocol). Do not modify or delete firewall/proxy logs. Preserve DLP alert data with full payload if available.' },
          { label: 'Identify Scope of Exfiltrated Data', detail: 'What data types: PII, financial, IP, credentials, health records? Volume transferred? Destination IP/domain — is it known threat actor infrastructure? Was data encrypted before exfiltration?' },
          { label: 'Isolate Compromised Systems', detail: 'If internal actor or compromised account: suspend account immediately. If external actor: isolate compromised host while preserving forensic state.' },
        ],
      },
      {
        title: 'Regulatory Assessment',
        type: 'table',
        headers: ['Regulation', 'Notification Threshold', 'Notification Deadline', 'Notify Whom'],
        rows: [
          ['GDPR', 'Personal data of EU residents', '72 hours of awareness', 'Supervisory Authority + affected individuals if high risk'],
          ['HIPAA', 'Protected Health Information (PHI)', '60 days of discovery', 'HHS + affected individuals (>500: media too)'],
          ['PCI DSS', 'Cardholder data', 'Immediately', 'Card brands + acquiring bank'],
          ['UK DPA 2018', 'Personal data', '72 hours', 'ICO + affected individuals if high risk'],
        ],
      },
      {
        title: 'Investigation Steps',
        type: 'steps',
        steps: [
          { label: 'Full Data Classification', detail: 'Work with customer data owner to classify exactly what was in the exfiltrated dataset. Document data types, record count, and individuals affected.' },
          { label: 'Attribution', detail: 'Analyse destination infrastructure (WHOIS, passive DNS, threat intel). Link to known threat actor groups if possible. This affects regulatory and law enforcement approach.' },
          { label: 'Notify Legal', detail: 'All external communications must be reviewed by legal before sending. Prepare breach notification letters with legal counsel.' },
          { label: 'Remediation', detail: 'Address root cause of breach: patch exploited vulnerability, secure misconfigured storage, revoke compromised credentials, implement DLP controls that were missing.' },
        ],
      },
    ],
  },
  {
    id: 'sop-ir-006',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-006: Insider Threat Response',
    severity: 'high',
    readTime: '7 min',
    tags: ['insider-threat', 'dlp', 'ueba', 'hr'],
    summary: 'Discreet investigation procedure for suspected insider threat activity, balancing evidence preservation with HR and legal requirements.',
    sections: [
      {
        title: '⚠️ Critical: Maintain Confidentiality',
        type: 'warning',
        content: 'Do NOT alert the subject. Do NOT access the investigation via the subject\'s shared systems. All evidence must be preserved legally — work with HR and Legal from the start. Improper handling can invalidate evidence in legal proceedings.',
      },
      {
        title: 'Investigation Steps',
        type: 'steps',
        steps: [
          { label: 'Receive & Validate Alert', detail: 'Source: UEBA alert, DLP trigger, manager report, HR notification. Validate against baseline behaviour before escalating. Distinguish anomaly from actual malicious intent.' },
          { label: 'Engage HR & Legal Immediately', detail: 'Before any investigation actions: brief HR and legal counsel. They define what evidence can be collected, how to handle it, and what actions can be taken against the employee.' },
          { label: 'Silent Monitoring', detail: 'Increase logging for the user account: DLP, email monitoring (within legal constraints), file access auditing, badge/physical access logs. Do NOT block yet — this alerts the subject.' },
          { label: 'Evidence Preservation', detail: 'Preserve: email archive, file access logs, DLP events, authentication logs, USB device logs, print logs, cloud sync logs. Hash all evidence files immediately to establish integrity.' },
          { label: 'Assess Scope', detail: 'What data was accessed or taken? Over what time period? Internal or external destination? Are there accomplices? Was this opportunistic or pre-planned?' },
          { label: 'Coordinated Account Actions', detail: 'When legal/HR authorise action: simultaneously disable account, revoke remote access, remove from distribution lists. Collect device for forensic imaging immediately.' },
          { label: 'Forensic Image', detail: 'Legal forensic image of workstation before returning to employee pool. Preserve browsing history, file timestamps, cloud sync state, application usage.' },
        ],
      },
    ],
  },
  {
    id: 'sop-ir-007',
    category: 'SOP',
    subcategory: 'Incident Response',
    title: 'SOP-IR-007: DDoS & Network Attack Response',
    severity: 'high',
    readTime: '6 min',
    tags: ['ddos', 'network', 'availability', 'firewall'],
    summary: 'Response to volumetric or application-layer DDoS attacks and malicious network activity targeting availability.',
    sections: [
      {
        title: 'Objective',
        content: 'Restore service availability as quickly as possible while collecting evidence for post-incident analysis and potential law enforcement referral.',
      },
      {
        title: 'Response Steps',
        type: 'steps',
        steps: [
          { label: 'Confirm DDoS vs. Outage', detail: 'Check uptime monitors, ISP telemetry, NetFlow data. Distinguish: volumetric flood (bandwidth saturation), protocol attack (SYN flood, Smurf), application layer (HTTP flood, Slowloris).' },
          { label: 'Activate DDoS Protection', detail: 'Enable cloud scrubbing (Cloudflare Magic Transit, AWS Shield Advanced, Azure DDoS Protection). Route traffic through scrubbing centre. ETA for mitigation: 5-15 minutes for cloud, immediate for on-prem appliance.' },
          { label: 'Contact ISP', detail: 'Request upstream null routing or blackhole routing of attack traffic. Provide: attacking IPs/ranges, attack vectors, timeline. ISP-level mitigation is required for volumetric attacks > provider capacity.' },
          { label: 'Rate Limiting & Blocking', detail: 'Apply rate limits on edge: SYN cookies for SYN floods. Geo-blocking if attack is concentrated from specific regions. Block attacking ASNs at firewall. Web application firewall rules for L7 attacks.' },
          { label: 'Monitor & Adjust', detail: 'Attack signatures change during an attack. Continuously monitor: traffic patterns, firewall block rates, scrubbing effectiveness. Adapt rules as attack evolves.' },
          { label: 'Evidence Collection', detail: 'Preserve NetFlow data, firewall logs, PCAP samples (not full capture — too large). Record attack start/peak/end times, volumes, source IPs/ranges, attack types.' },
          { label: 'Post-Attack Review', detail: 'Root cause: why was infrastructure vulnerable? Were capacity limits too low? DDoS protection not pre-activated? Update runbook with attack specifics for future reference.' },
        ],
      },
    ],
  },

  // ══ ANALYSIS GUIDES ══
  {
    id: 'guide-mitre',
    category: 'Guide',
    subcategory: 'Threat Analysis',
    title: 'MITRE ATT&CK Quick Reference for SOC Analysts',
    severity: 'info',
    readTime: '10 min',
    tags: ['mitre', 'att&ck', 'ttp', 'threat-intel'],
    summary: 'Key MITRE ATT&CK tactics and commonly observed techniques mapped to detection opportunities in Sentinel.',
    sections: [
      {
        title: 'Tactics Overview',
        type: 'table',
        headers: ['Tactic', 'ID', 'What Adversaries Are Doing', 'Key Detection Sources'],
        rows: [
          ['Reconnaissance', 'TA0043', 'Scanning, OSINT, phishing for info', 'External threat intel, honeypot, DNS logs'],
          ['Initial Access', 'TA0001', 'Phishing, exploit public-facing app, supply chain', 'Email gateway, WAF, Sentinel alerts'],
          ['Execution', 'TA0002', 'PowerShell, WMI, scheduled tasks, scripts', 'EDR process events, Sysmon Event 1/4648'],
          ['Persistence', 'TA0003', 'Registry run keys, services, startup folders', 'EDR registry events, Sysmon Event 12/13'],
          ['Privilege Escalation', 'TA0004', 'Token impersonation, UAC bypass, exploits', 'Windows Event 4672, 4673, EDR alerts'],
          ['Defense Evasion', 'TA0005', 'Log clearing, AMSI bypass, process injection', 'Event 1102, Sysmon Event 8/10, EDR'],
          ['Credential Access', 'TA0006', 'LSASS dump, Kerberoasting, credential spraying', 'Event 4648/4769/4771, EDR LSASS alerts'],
          ['Discovery', 'TA0007', 'AD enumeration, network scans, service discovery', 'Event 4624/4625, Sysmon Event 3, LDAP logs'],
          ['Lateral Movement', 'TA0008', 'Pass-the-hash, RDP, SMB, WMI', 'Event 4648/4624 Type 3, network flow logs'],
          ['Collection', 'TA0009', 'File staging, clipboard data, keylogging', 'DLP, EDR file events, Sysmon Event 11'],
          ['Exfiltration', 'TA0010', 'HTTPS, DNS tunneling, cloud storage upload', 'Proxy logs, DNS analytics, NetFlow'],
          ['Impact', 'TA0040', 'Ransomware, data destruction, service disruption', 'EDR, backup system alerts, availability monitors'],
        ],
      },
      {
        title: 'Top 10 Most Observed Techniques (2024-2025)',
        type: 'table',
        headers: ['Technique', 'ID', 'Common Tools', 'Detection KQL Table'],
        rows: [
          ['Phishing', 'T1566', 'Custom lures, adversary-in-middle kits', 'EmailEvents, UrlClickEvents'],
          ['PowerShell', 'T1059.001', 'Cobalt Strike, Empire, custom scripts', 'DeviceProcessEvents, SecurityEvent 4104'],
          ['Valid Accounts', 'T1078', 'Stolen credentials, MFA bypass', 'SigninLogs, AuditLogs'],
          ['OS Credential Dumping', 'T1003', 'Mimikatz, ProcDump, comsvcs.dll', 'DeviceProcessEvents, Event 4688'],
          ['Remote Services: RDP', 'T1021.001', 'Native Windows RDP', 'DeviceLogonEvents, SecurityEvent 4624 Type 10'],
          ['Scheduled Task/Job', 'T1053', 'schtasks.exe, at.exe, WMI', 'DeviceProcessEvents, Event 4698'],
          ['Ingress Tool Transfer', 'T1105', 'certutil, bitsadmin, PowerShell IWR', 'DeviceNetworkEvents, CommonSecurityLog'],
          ['Obfuscated Files/Info', 'T1027', 'Base64, XOR, packers', 'DeviceFileEvents, Sysmon Event 1'],
          ['Command-Line Interface', 'T1059.003', 'cmd.exe, wscript, mshta', 'DeviceProcessEvents, Event 4688'],
          ['Exfil Over Web Service', 'T1567', 'OneDrive, Dropbox, Mega, Rclone', 'DeviceNetworkEvents, CloudAppEvents'],
        ],
      },
    ],
  },
  {
    id: 'guide-event-ids',
    category: 'Guide',
    subcategory: 'Threat Analysis',
    title: 'Critical Windows Event IDs Reference',
    severity: 'info',
    readTime: '8 min',
    tags: ['windows', 'event-ids', 'siem', 'logging'],
    summary: 'The most security-relevant Windows Event IDs, what they mean, and when they indicate malicious activity.',
    sections: [
      {
        title: 'Authentication & Account Events',
        type: 'table',
        headers: ['Event ID', 'Description', 'Malicious Indicator'],
        rows: [
          ['4624', 'Successful logon', 'Logon Type 3 (network) from unusual host; Type 10 (RemoteInteractive/RDP)'],
          ['4625', 'Failed logon', 'High volume from single source = brute force; distributed = credential spray'],
          ['4648', 'Logon using explicit credentials', 'Lateral movement — process logging on as different user (e.g. Mimikatz pass-the-hash)'],
          ['4672', 'Special privileges assigned at logon', 'Admin logon — watch for unusual accounts getting SeDebugPrivilege'],
          ['4697', 'Service installed in system', 'Malware persistence via new service install'],
          ['4698', 'Scheduled task created', 'Malware persistence; correlate with process that created it'],
          ['4720', 'User account created', 'Backdoor account creation by attacker'],
          ['4732', 'Member added to security-enabled local group', 'Adding to Administrators group — privilege escalation'],
          ['4768', 'Kerberos TGT requested', 'Baseline normal; unusual patterns indicate Golden Ticket or AS-REP Roasting'],
          ['4769', 'Kerberos service ticket requested', 'High volume of RC4 encryption requests = Kerberoasting attack'],
          ['4771', 'Kerberos pre-auth failed', 'Brute force or AS-REP Roasting attempt against accounts without pre-auth'],
          ['4776', 'NTLM authentication attempt', 'Pass-the-hash if from different host than where user logged in'],
        ],
      },
      {
        title: 'Process & Command Execution Events',
        type: 'table',
        headers: ['Event ID', 'Log Source', 'Description', 'Malicious Indicator'],
        rows: [
          ['4104', 'PowerShell', 'Script block logging', 'Obfuscated commands, download cradles, Invoke-Expression with encoded content'],
          ['4688', 'Security', 'Process creation with command line', 'Requires audit policy; watch for: cmd spawning from Office, PowerShell -EncodedCommand, LOLBAS abuse'],
          ['1 (Sysmon)', 'Sysmon', 'Process create', 'Parent-child process anomalies: Word → cmd, Excel → PowerShell'],
          ['7 (Sysmon)', 'Sysmon', 'Image loaded', 'DLL side-loading; unexpected DLLs loaded by trusted processes'],
          ['8 (Sysmon)', 'Sysmon', 'CreateRemoteThread', 'Process injection — always suspicious when source ≠ destination'],
          ['10 (Sysmon)', 'Sysmon', 'ProcessAccess', 'LSASS access by non-system processes = credential dumping'],
          ['1102', 'Security', 'Audit log cleared', 'Attacker covering tracks — always investigate'],
        ],
      },
    ],
  },
  {
    id: 'guide-kql',
    category: 'Guide',
    subcategory: 'Threat Analysis',
    title: 'KQL Quick Reference — Sentinel Hunting Queries',
    severity: 'info',
    readTime: '12 min',
    tags: ['kql', 'sentinel', 'hunting', 'queries'],
    summary: 'Ready-to-use KQL queries for common threat hunting scenarios in Microsoft Sentinel / Log Analytics.',
    sections: [
      {
        title: 'Authentication Anomalies',
        type: 'code',
        lang: 'kql',
        snippets: [
          { label: 'Password spray detection', code: '// Many users, few attempts each — classic spray pattern\nSecurityEvent\n| where EventID == 4625\n| where TimeGenerated > ago(1h)\n| summarize TargetCount=dcount(TargetUserName), FailCount=count() by IpAddress\n| where TargetCount > 10 and FailCount > 20\n| sort by TargetCount desc' },
          { label: 'Brute force detection', code: '// Single target, many attempts\nSecurityEvent\n| where EventID == 4625\n| where TimeGenerated > ago(1h)\n| summarize FailCount=count() by TargetUserName, IpAddress\n| where FailCount > 30\n| sort by FailCount desc' },
          { label: 'Successful login after many failures', code: 'let failures = SecurityEvent\n| where EventID == 4625\n| where TimeGenerated > ago(1h)\n| summarize FailCount=count() by TargetUserName, IpAddress;\nSecurityEvent\n| where EventID == 4624\n| join kind=inner failures on TargetUserName, IpAddress\n| where FailCount > 10\n| project TimeGenerated, TargetUserName, IpAddress, FailCount' },
        ],
      },
      {
        title: 'Lateral Movement',
        type: 'code',
        lang: 'kql',
        snippets: [
          { label: 'Detect RDP lateral movement', code: 'SecurityEvent\n| where EventID == 4624\n| where LogonType == 10  // RemoteInteractive\n| where AccountType == "User"\n| where WorkstationName != Computer\n| project TimeGenerated, Account, WorkstationName, Computer, IpAddress\n| sort by TimeGenerated desc' },
          { label: 'Pass-the-hash indicator', code: 'SecurityEvent\n| where EventID == 4624\n| where LogonType == 3  // Network\n| where AuthenticationPackageName == "NTLM"\n| where WorkstationName != Computer\n| summarize Count=count() by Account, IpAddress, WorkstationName, Computer\n| where Count > 5\n| sort by Count desc' },
          { label: 'SMB lateral movement (Sysmon)', code: 'Sysmon\n| where EventID == 3  // Network connection\n| where DestinationPort == 445\n| where InitiatingProcessFileName !in ("System", "svchost.exe")\n| project TimeGenerated, Computer, InitiatingProcessFileName, DestinationIp, DestinationPort\n| sort by TimeGenerated desc' },
        ],
      },
      {
        title: 'Persistence Mechanisms',
        type: 'code',
        lang: 'kql',
        snippets: [
          { label: 'New scheduled task', code: 'SecurityEvent\n| where EventID == 4698\n| extend TaskName = tostring(EventData.TaskName)\n| extend TaskContent = tostring(EventData.TaskContent)\n| project TimeGenerated, Computer, SubjectUserName, TaskName, TaskContent\n| sort by TimeGenerated desc' },
          { label: 'Suspicious service installation', code: 'SecurityEvent\n| where EventID == 4697\n| extend ServiceName = tostring(EventData.ServiceName)\n| extend ServiceFileName = tostring(EventData.ServiceFileName)\n| where ServiceFileName has_any ("%temp%", "%appdata%", "powershell", "cmd.exe", "wscript")\n| project TimeGenerated, Computer, SubjectUserName, ServiceName, ServiceFileName' },
          { label: 'Registry run key modification', code: 'Sysmon\n| where EventID in (12, 13)\n| where TargetObject has_any ("\\\\Run\\\\", "\\\\RunOnce\\\\", "\\\\Winlogon\\\\")\n| where not (Image has_any ("\\\\Windows\\\\", "\\\\Program Files\\\\"))\n| project TimeGenerated, Computer, Image, TargetObject, Details' },
        ],
      },
    ],
  },

  // ══ QUICK REFERENCE ══
  {
    id: 'ref-escalation',
    category: 'Reference',
    subcategory: 'Operations',
    title: 'SOC Escalation Matrix',
    severity: 'info',
    readTime: '3 min',
    tags: ['escalation', 'contacts', 'sla', 'process'],
    summary: 'Who to contact, when, and how for each incident severity level.',
    sections: [
      {
        title: 'Escalation Tiers',
        type: 'table',
        headers: ['Tier', 'Role', 'Handles', 'Escalates To'],
        rows: [
          ['Tier 1', 'SOC Analyst', 'Alert triage, P3/P4 incidents, initial containment', 'Tier 2 for P1/P2 or complex investigations'],
          ['Tier 2', 'Senior SOC Analyst', 'P2 incidents, threat hunting, malware analysis', 'Tier 3 for P1 or nation-state TTPs'],
          ['Tier 3', 'SOC Specialist / DFIR', 'P1 incidents, forensics, advanced persistent threats', 'SOC Manager for executive comms'],
          ['SOC Manager', 'SOC Manager', 'P1 oversight, customer exec comms, resource allocation', 'CISO for business impact decisions'],
        ],
      },
      {
        title: 'Response Time SLAs',
        type: 'table',
        headers: ['Severity', 'Detect → Acknowledge', 'Acknowledge → Contain', 'Contain → Resolve', 'Post-Incident Report'],
        rows: [
          ['P1 Critical', '15 min', '30 min', '4 hours', '24 hours'],
          ['P2 High', '1 hour', '4 hours', '24 hours', '72 hours'],
          ['P3 Medium', '4 hours', '24 hours', '72 hours', '1 week'],
          ['P4 Low', '24 hours', '72 hours', '1 week', 'Monthly report'],
        ],
      },
    ],
  },
  {
    id: 'ref-ioc-types',
    category: 'Reference',
    subcategory: 'Threat Intel',
    title: 'IOC Types & Investigation Guide',
    severity: 'info',
    readTime: '5 min',
    tags: ['ioc', 'threat-intel', 'investigation', 'pivoting'],
    summary: 'How to handle, validate and pivot on different indicator types during a threat investigation.',
    sections: [
      {
        title: 'IOC Types & Investigation Steps',
        type: 'table',
        headers: ['IOC Type', 'Where to Look', 'Validation Steps', 'Pivot To'],
        rows: [
          ['IP Address', 'Firewall logs, NetFlow, Sentinel', 'VirusTotal, Shodan, Whois, passive DNS, threat intel feed', 'Domain names, ASN, other hosts connecting to same IP'],
          ['Domain/URL', 'DNS logs, proxy logs, email gateway', 'VirusTotal, URLScan.io, urlhaus.abuse.ch, creation date', 'Registrant email, hosting IP, subdomains, certificates'],
          ['File Hash (SHA256)', 'EDR telemetry, email gateway', 'VirusTotal, MalwareBazaar, Any.run sandbox', 'Filename, signer, PE imports, network indicators in sandbox'],
          ['Email Address', 'Email gateway, Sentinel', 'VirusTotal, hunter.io, have i been pwned, breach data', 'Associated domains, phone numbers, other accounts'],
          ['Certificate (SHA1)', 'Network SSL inspection, cert transparency logs', 'crt.sh, Censys, Shodan', 'All domains sharing same cert, IP infrastructure'],
          ['Registry Key', 'EDR registry telemetry, Sysmon events', 'Is it known malware persistence key? Any.run, Cuckoo', 'Files written to associated paths, processes that set it'],
          ['Mutex', 'Memory forensics, sandbox', 'Sandbox report, malware families database', 'Malware family, other samples creating same mutex'],
        ],
      },
      {
        title: 'IOC Confidence Levels',
        type: 'table',
        headers: ['Confidence', 'Criteria', 'Action'],
        rows: [
          ['High', 'Multiple independent sources confirm malicious; seen in active campaigns', 'Block immediately, share with MISP/TIP'],
          ['Medium', 'Single source confirms; contextual evidence aligns', 'Monitor + alert; block pending further confirmation'],
          ['Low', 'Suspicious but not confirmed; could be legitimate', 'Add to watchlist; do not block without investigation'],
          ['FP (False Positive)', 'Confirmed legitimate infrastructure', 'Whitelist with justification; document to prevent recurrence'],
        ],
      },
    ],
  },
];

const CATEGORIES = ['All', 'SOP', 'Guide', 'Reference'];
const SEVERITY_CONFIG = {
  critical: { label: 'Critical', cls: 'kb-sev--critical' },
  high:     { label: 'High',     cls: 'kb-sev--high'     },
  medium:   { label: 'Medium',   cls: 'kb-sev--medium'   },
  info:     { label: 'Info',     cls: 'kb-sev--info'     },
};
const CATEGORY_ICONS = { SOP: Shield, Guide: BookOpen, Reference: FileText };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button className="kb-copy-btn" onClick={handle} title="Copy">
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

function Section({ section }) {
  const [open, setOpen] = useState(true);

  const headerClass = `kb-section__header${section.type === 'warning' ? ' kb-section__header--warning' : ''}`;

  return (
    <div className={`kb-section${section.type === 'warning' ? ' kb-section--warning' : ''}`}>
      <button className={headerClass} onClick={() => setOpen((o) => !o)}>
        <span className="kb-section__title">{section.title}</span>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
      </button>

      {open && (
        <div className="kb-section__body">
          {section.type === 'warning' && (
            <div className="kb-warning-box">
              <AlertTriangle size={16} />
              <p>{section.content}</p>
            </div>
          )}

          {section.type === 'info' && (
            <div className="kb-info-box">
              <Info size={14} />
              <pre className="kb-info-box__pre">{section.content}</pre>
            </div>
          )}

          {(!section.type || section.type === 'text') && section.content && (
            <p className="kb-section__text">{section.content}</p>
          )}

          {section.type === 'steps' && (
            <ol className="kb-steps">
              {section.steps.map((step, i) => (
                <li key={i} className="kb-step">
                  <div className="kb-step__num">{i + 1}</div>
                  <div className="kb-step__content">
                    <strong className="kb-step__label">{step.label}</strong>
                    <p className="kb-step__detail">{step.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {section.type === 'table' && (
            <div className="kb-table-wrap">
              <table className="kb-table">
                <thead>
                  <tr>{section.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {section.rows.map((row, i) => (
                    <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {section.type === 'code' && (
            <div className="kb-snippets">
              {section.snippets.map((s, i) => (
                <div key={i} className="kb-snippet">
                  <div className="kb-snippet__header">
                    <Terminal size={12} />
                    <span className="kb-snippet__label">{s.label}</span>
                    <CopyButton text={s.code} />
                  </div>
                  <pre className="kb-snippet__code">{s.code}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ArticleView({ article, onBack }) {
  const SevCfg = SEVERITY_CONFIG[article.severity];
  return (
    <div className="kb-article">
      <button className="kb-back-btn" onClick={onBack}>
        <ArrowLeft size={15} /> Back to Knowledge Base
      </button>

      <div className="kb-article__header">
        <div className="kb-article__meta">
          <span className={`kb-sev ${SevCfg.cls}`}>{SevCfg.label}</span>
          <span className="kb-cat-chip">{article.category}</span>
          <span className="kb-sub-chip">{article.subcategory}</span>
          <span className="kb-time"><Clock size={11} /> {article.readTime} read</span>
        </div>
        <h1 className="kb-article__title">{article.title}</h1>
        <p className="kb-article__summary">{article.summary}</p>
        <div className="kb-article__tags">
          {article.tags.map((t) => (
            <span key={t} className="kb-tag"><Tag size={10} />{t}</span>
          ))}
        </div>
      </div>

      <div className="kb-article__sections">
        {article.sections.map((sec, i) => <Section key={i} section={sec} />)}
      </div>
    </div>
  );
}

function ArticleCard({ article, onClick }) {
  const SevCfg = SEVERITY_CONFIG[article.severity];
  const Icon = CATEGORY_ICONS[article.category] || FileText;
  return (
    <div className="kb-card" onClick={onClick}>
      <div className="kb-card__top">
        <div className="kb-card__icon-wrap">
          <Icon size={18} />
        </div>
        <span className={`kb-sev ${SevCfg.cls}`}>{SevCfg.label}</span>
      </div>
      <h3 className="kb-card__title">{article.title}</h3>
      <p className="kb-card__summary">{article.summary}</p>
      <div className="kb-card__footer">
        <span className="kb-card__cat">{article.subcategory}</span>
        <span className="kb-time"><Clock size={10} />{article.readTime}</span>
        <ChevronRight size={14} className="kb-card__arrow" />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function KnowledgeBase() {
  const [search, setSearch]         = useState('');
  const [category, setCategory]     = useState('All');
  const [selected, setSelected]     = useState(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return ARTICLES.filter((a) => {
      const matchQ = !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q) || a.tags.some((t) => t.includes(q));
      const matchC = category === 'All' || a.category === category;
      return matchQ && matchC;
    });
  }, [search, category]);

  const stats = useMemo(() => ({
    sops:   ARTICLES.filter((a) => a.category === 'SOP').length,
    guides: ARTICLES.filter((a) => a.category === 'Guide').length,
    refs:   ARTICLES.filter((a) => a.category === 'Reference').length,
  }), []);

  if (selected) {
    return (
      <div className="knowledge-base">
        <ArticleView article={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div className="knowledge-base">
      {/* Header */}
      <div className="kb-header">
        <div className="kb-header__title-row">
          <BookOpen size={22} className="kb-header__icon" />
          <div>
            <h1 className="kb-header__title">SOC Knowledge Base</h1>
            <p className="kb-header__subtitle">Standard Operating Procedures, threat analysis guides, and quick references for SOC analysts</p>
          </div>
        </div>
        <div className="kb-stats">
          <div className="kb-stat">
            <Shield size={14} /><span className="kb-stat__val">{stats.sops}</span><span className="kb-stat__label">SOPs</span>
          </div>
          <div className="kb-stat">
            <BookOpen size={14} /><span className="kb-stat__val">{stats.guides}</span><span className="kb-stat__label">Guides</span>
          </div>
          <div className="kb-stat">
            <FileText size={14} /><span className="kb-stat__val">{stats.refs}</span><span className="kb-stat__label">References</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="kb-toolbar">
        <div className="kb-search">
          <Search size={14} className="kb-search__icon" />
          <input
            className="kb-search__input"
            placeholder="Search SOPs, guides, techniques…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="kb-cat-group">
          {CATEGORIES.map((c) => (
            <button key={c} className={`kb-cat-btn${category === c ? ' kb-cat-btn--active' : ''}`} onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="kb-empty">
          <Search size={32} />
          <p>No articles match your search.</p>
        </div>
      ) : (
        <>
          {/* Group by subcategory within selected category */}
          {['Incident Response', 'Threat Analysis', 'Operations', 'Threat Intel'].map((sub) => {
            const items = filtered.filter((a) => a.subcategory === sub);
            if (!items.length) return null;
            return (
              <div key={sub} className="kb-group">
                <h2 className="kb-group__title">{sub}</h2>
                <div className="kb-grid">
                  {items.map((a) => (
                    <ArticleCard key={a.id} article={a} onClick={() => setSelected(a)} />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
